import React, { useState, useEffect } from 'react';
import './GameDetail.css';

function GameDetail({ game, onClose, onAddToBetSlip }) {
  const [activeMarket, setActiveMarket] = useState('moneyline');
  const [gameStats, setGameStats] = useState(null);

  useEffect(() => {
    // Simulate fetching detailed game stats
    loadGameDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.id]);

  const loadGameDetails = () => {
    // In production, fetch real stats from API
    setGameStats({
      attendance: '18,064',
      weather: game.league === 'NFL' ? '72°F, Clear' : 'Indoor',
      broadcast: 'ESPN',
      series: '1-1',
      lastMeetings: [
        { date: 'Jan 15', winner: game.home, score: '112-108' },
        { date: 'Dec 20', winner: game.away, score: '105-98' }
      ]
    });
  };

  // Generate betting markets
  const markets = {
    moneyline: {
      title: 'Moneyline',
      description: 'Pick the winner',
      options: [
        { team: game.away, odds: game.awayOdds, line: null },
        { team: game.home, odds: game.homeOdds, line: null }
      ]
    },
    spread: {
      title: 'Point Spread',
      description: 'Win margin prediction',
      options: [
        { team: game.away, odds: -110, line: game.awayOdds > game.homeOdds ? '+3.5' : '-3.5' },
        { team: game.home, odds: -110, line: game.homeOdds > game.awayOdds ? '+3.5' : '-3.5' }
      ]
    },
    total: {
      title: 'Over/Under',
      description: 'Total points scored',
      options: [
        { team: 'Over', odds: -110, line: '215.5' },
        { team: 'Under', odds: -110, line: '215.5' }
      ]
    },
    firstHalf: {
      title: '1st Half',
      description: 'First half winner',
      options: [
        { team: game.away, odds: game.awayOdds + 20, line: null },
        { team: game.home, odds: game.homeOdds + 20, line: null }
      ]
    },
    teamTotal: {
      title: 'Team Totals',
      description: 'Individual team points',
      options: [
        { team: `${game.away} Over`, odds: -110, line: '108.5' },
        { team: `${game.away} Under`, odds: -110, line: '108.5' },
        { team: `${game.home} Over`, odds: -115, line: '107.5' },
        { team: `${game.home} Under`, odds: +105, line: '107.5' }
      ]
    },
    quarters: {
      title: 'Quarter Betting',
      description: 'Individual quarter outcomes',
      options: [
        { team: `1Q ${game.away}`, odds: +110, line: null },
        { team: `1Q ${game.home}`, odds: -130, line: null },
        { team: `2Q ${game.away}`, odds: +105, line: null },
        { team: `2Q ${game.home}`, odds: -125, line: null }
      ]
    }
  };

  const handleBetClick = (market, option) => {
    const bet = {
      id: `${game.id}-${market}-${option.team}`,
      game: `${game.away} @ ${game.home}`,
      team: option.team,
      odds: option.odds,
      league: game.league,
      market: markets[market].title,
      line: option.line
    };
    onAddToBetSlip(bet);
  };

  return (
    <div className="game-detail-overlay" onClick={onClose}>
      <div className="game-detail-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="detail-close" onClick={onClose}>×</button>

        {/* Game Header */}
        <div className="game-detail-header">
          <div className="league-info">
            <span className="league-badge-large">{game.league}</span>
            <span className="game-time-large">{game.time}</span>
          </div>

          <div className="matchup-display">
            <div className="team-display away-team">
              <div className="team-logo">{game.away.charAt(0)}</div>
              <h2>{game.away}</h2>
              <div className="team-record">24-18 (Away: 12-10)</div>
            </div>

            <div className="vs-divider">
              <span>VS</span>
            </div>

            <div className="team-display home-team">
              <div className="team-logo">{game.home.charAt(0)}</div>
              <h2>{game.home}</h2>
              <div className="team-record">28-14 (Home: 16-5)</div>
            </div>
          </div>

          {/* Game Info */}
          {gameStats && (
            <div className="game-info-bar">
              <div className="info-item">
                <span className="info-label">Venue</span>
                <span className="info-value">{game.venue || 'TBA'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Broadcast</span>
                <span className="info-value">{gameStats.broadcast}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Attendance</span>
                <span className="info-value">{gameStats.attendance}</span>
              </div>
              {gameStats.weather !== 'Indoor' && (
                <div className="info-item">
                  <span className="info-label">Weather</span>
                  <span className="info-value">{gameStats.weather}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Betting Markets */}
        <div className="betting-markets">
          <div className="market-tabs">
            <button
              className={activeMarket === 'moneyline' ? 'active' : ''}
              onClick={() => setActiveMarket('moneyline')}
            >
              Moneyline
            </button>
            <button
              className={activeMarket === 'spread' ? 'active' : ''}
              onClick={() => setActiveMarket('spread')}
            >
              Spread
            </button>
            <button
              className={activeMarket === 'total' ? 'active' : ''}
              onClick={() => setActiveMarket('total')}
            >
              Total
            </button>
            <button
              className={activeMarket === 'firstHalf' ? 'active' : ''}
              onClick={() => setActiveMarket('firstHalf')}
            >
              1st Half
            </button>
            <button
              className={activeMarket === 'teamTotal' ? 'active' : ''}
              onClick={() => setActiveMarket('teamTotal')}
            >
              Team Totals
            </button>
            <button
              className={activeMarket === 'quarters' ? 'active' : ''}
              onClick={() => setActiveMarket('quarters')}
            >
              Quarters
            </button>
          </div>

          <div className="market-content">
            <div className="market-header">
              <h3>{markets[activeMarket].title}</h3>
              <p>{markets[activeMarket].description}</p>
            </div>

            <div className="betting-options">
              {markets[activeMarket].options.map((option, index) => (
                <div key={index} className="bet-option">
                  <div className="bet-option-info">
                    <span className="bet-team">{option.team}</span>
                    {option.line && <span className="bet-line">{option.line}</span>}
                  </div>
                  <button
                    className="bet-odds-btn"
                    onClick={() => handleBetClick(activeMarket, option)}
                  >
                    {option.odds > 0 ? '+' : ''}{option.odds}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Matchups */}
          {gameStats && (
            <div className="recent-matchups">
              <h4>Recent Matchups</h4>
              <div className="matchup-list">
                {gameStats.lastMeetings.map((meeting, index) => (
                  <div key={index} className="matchup-item">
                    <span className="matchup-date">{meeting.date}</span>
                    <span className="matchup-result">
                      {meeting.winner} won {meeting.score}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trends & Insights */}
          <div className="betting-insights">
            <h4>Betting Trends</h4>
            <div className="insights-grid">
              <div className="insight-card">
                <div className="insight-label">Public Money</div>
                <div className="insight-value">{game.home}: 68%</div>
                <div className="insight-bar">
                  <div className="insight-fill" style={{width: '68%'}}></div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-label">Sharp Money</div>
                <div className="insight-value">{game.away}: 58%</div>
                <div className="insight-bar">
                  <div className="insight-fill sharp" style={{width: '58%'}}></div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-label">Line Movement</div>
                <div className="insight-value">-2.5 → -3.5</div>
              </div>
              <div className="insight-card">
                <div className="insight-label">Total Movement</div>
                <div className="insight-value">217.5 → 215.5</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GameDetail;
